import React, { useEffect, useState } from 'react';
import { usePendingPath } from '@/hooks/routing/usePendingPath';
import { cn } from '@/lib/utils';

const APPEAR_DELAY_MS = 250;
const MIN_VISIBLE_MS = 400;
const FADE_OUT_MS = 200;

type Phase = 'idle' | 'running' | 'finishing';

const RouteProgressBar: React.FC = () => {
  const isLoading = usePendingPath() !== null;
  const [phase, setPhase] = useState<Phase>('idle');
  const [shownAt, setShownAt] = useState(0);

  useEffect(() => {
    if (!isLoading) return;

    const timer = setTimeout(() => {
      setShownAt(Date.now());
      setPhase('running');
    }, APPEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    if (isLoading || phase === 'idle') return;

    if (phase === 'running') {
      const held = Date.now() - shownAt;
      const timer = setTimeout(() => setPhase('finishing'), Math.max(0, MIN_VISIBLE_MS - held));
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => setPhase('idle'), FADE_OUT_MS);
    return () => clearTimeout(timer);
  }, [isLoading, phase, shownAt]);

  if (phase === 'idle') return null;

  return (
    <div
      role="progressbar"
      aria-label="Loading page"
      className="fixed inset-x-0 top-0 z-110 h-0.5 pointer-events-none"
    >
      <div
        key={shownAt}
        className={cn(
          'h-full w-full origin-left bg-primary animate-route-progress',
          phase === 'finishing' && 'opacity-0 transition-opacity duration-200'
        )}
      />
    </div>
  );
};

export default RouteProgressBar;
